import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    hashKey,
    keepPreviousData,
    QueryKey,
    useQueries,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import {
    summarizeBatchResults,
    useRetryFailedBatches,
} from "./loadMoreBatchResults";
import {
    appendNextSlots,
    type LoadMoreSlot,
    mergeLoadedPages,
} from "./useLoadMorePages.lib";

interface LoadMorePageQuery<TResponse> {
    queryKey: QueryKey;
    queryFn: () => Promise<TResponse>;
}

interface UseLoadMorePagesParams<TResponse, TRow, TId> {
    /** Key and fetcher for the 1-based batch `pageNumber`. */
    buildPageQuery: (pageNumber: number) => LoadMorePageQuery<TResponse>;
    /** Rows of one batch response. Keep it stable (module scope or memoized). */
    select: (response: TResponse) => TRow[];
    /** Keep it stable (module scope or memoized). */
    getRowId: (row: TRow) => TId;
    pageSize: number;
    total?: number;
    enabled?: boolean;
}

export interface LoadMorePages<TRow> {
    /** Every loaded row; `undefined` until the first batch arrives. */
    rows: TRow[] | undefined;
    /** Also true while a requested batch is loading or failed. */
    hasMore: boolean;
    isLoadingMore: boolean;
    isLoadMoreError: boolean;
    isFirstPageFetching: boolean;
    loadNextPage: () => void;
    retry: () => void;
    /** Keys of the batches holding data, for writers that patch cached rows. */
    loadedQueryKeys: QueryKey[];
    /** Changes whenever the list starts over from the first batch. */
    resetKey: string;
}

const NO_SLOTS: LoadMoreSlot[] = [];
const NO_SHIFTS = { count: 0, inFlightCount: 0 };

/**
 * Loads a list one batch at a time and keeps one normal query per batch, so
 * cache writers and `invalidateQueries` prefixes that expect a single page
 * keep working. Anything that changes batch 1's key (filters, sort, scope)
 * starts the list over from batch 1; batch 1 keeps showing the previous rows
 * until the new ones arrive, as a paged table does.
 */
export function useLoadMorePages<TResponse, TRow, TId>({
    buildPageQuery,
    select,
    getRowId,
    pageSize,
    total,
    enabled = true,
}: UseLoadMorePagesParams<TResponse, TRow, TId>): LoadMorePages<TRow> {
    const firstPageQuery = useMemo(() => buildPageQuery(1), [buildPageQuery]);
    const resetKey = hashKey(firstPageQuery.queryKey);
    const [slotsState, setSlotsState] = useState({
        resetKey,
        visit: 0,
        slots: NO_SLOTS,
        shifts: NO_SHIFTS,
    });
    const isCurrentList = slotsState.resetKey === resetKey;
    const visit = isCurrentList ? slotsState.visit : slotsState.visit + 1;
    if (!isCurrentList) {
        setSlotsState({ resetKey, visit, slots: NO_SLOTS, shifts: NO_SHIFTS });
    }
    const slots = isCurrentList ? slotsState.slots : NO_SLOTS;
    const shifts = isCurrentList ? slotsState.shifts : NO_SHIFTS;
    const slotQueries = slots.map((slot, index) => {
        const query = buildPageQuery(slot.pageNumber);
        return {
            ...query,
            queryKey: [
                ...query.queryKey,
                slot.type === "page"
                    ? { loadMoreVisit: visit }
                    : { loadMoreVisit: visit, loadMoreGap: index },
            ],
            // A later batch only reflects the edits this visit has counted.
            gcTime: 0,
        };
    });

    const firstPage = useQuery({
        ...firstPageQuery,
        enabled,
        placeholderData: keepPreviousData,
    });
    const slotResults = useQueries({
        queries: slotQueries.map((query) => ({
            ...query,
            enabled,
        })),
        combine: summarizeBatchResults<TResponse>,
    });

    const firstPageResponse = firstPage.data;
    const isFirstPageFromPreviousList = firstPage.isPlaceholderData;
    const merged = useMemo(() => {
        if (firstPageResponse === undefined) {
            return undefined;
        }
        return mergeLoadedPages({
            pages: [firstPageResponse, ...slotResults.responses].map(
                (response) =>
                    response === undefined ? undefined : select(response),
            ),
            getRowId,
            pageSize,
            total,
            shiftCount: shifts.count,
        });
    }, [
        firstPageResponse,
        slotResults.responses,
        select,
        getRowId,
        pageSize,
        total,
        shifts.count,
    ]);

    const isNextBatchRequested =
        slotResults.isAnyBatchPending || slotResults.isAnyBatchError;
    const hasMore =
        !isFirstPageFromPreviousList &&
        (isNextBatchRequested || (merged?.hasMore ?? false));
    const canLoadNextPage =
        hasMore &&
        !slotResults.isAnyBatchPending &&
        !slotResults.isAnyBatchError;

    const loadNextPage = useCallback(() => {
        if (!canLoadNextPage) {
            return;
        }
        setSlotsState((previous) => {
            const isPreviousList = previous.resetKey === resetKey;
            const previousSlots = isPreviousList ? previous.slots : NO_SLOTS;
            if (previousSlots !== slots) {
                return previous;
            }
            const previousShifts = isPreviousList ? previous.shifts : NO_SHIFTS;
            return {
                resetKey,
                visit,
                slots: appendNextSlots({
                    slots,
                    shiftCount: previousShifts.count,
                    inFlightShiftCount: previousShifts.inFlightCount,
                    pageSize,
                }),
                shifts: NO_SHIFTS,
            };
        });
    }, [canLoadNextPage, resetKey, visit, slots, pageSize]);

    const isAnyBatchPendingRef = useLatestRef(slotResults.isAnyBatchPending);
    const recordShift = useCallback(() => {
        setSlotsState((previous) => {
            const isPreviousList = previous.resetKey === resetKey;
            const previousShifts = isPreviousList ? previous.shifts : NO_SHIFTS;
            return {
                resetKey,
                visit,
                slots: isPreviousList ? previous.slots : NO_SLOTS,
                shifts: {
                    count: previousShifts.count + 1,
                    inFlightCount:
                        previousShifts.inFlightCount +
                        (isAnyBatchPendingRef.current ? 1 : 0),
                },
            };
        });
    }, [resetKey, visit, isAnyBatchPendingRef]);

    const retry = useRetryFailedBatches(slotResults.failedRefetches);

    const isFirstPageLoaded =
        firstPageResponse !== undefined && !isFirstPageFromPreviousList;
    const loadedQueryKeys = [
        ...(isFirstPageLoaded ? [firstPageQuery.queryKey] : []),
        ...slotQueries
            .filter((_, index) => slotResults.responses[index] !== undefined)
            .map((query) => query.queryKey),
    ];

    // Any local write to a loaded batch (a save, a hide, an inline review) may
    // drop a row from the server's list, so it counts as a possible shift.
    // One edit writes every loaded batch in the same tick, so a tick counts once.
    const queryClient = useQueryClient();
    const loadedQueryHashesRef = useLatestRef(
        new Set(loadedQueryKeys.map((queryKey) => hashKey(queryKey))),
    );
    const isShiftRecordedThisTickRef = useRef(false);
    useEffect(() => {
        if (!enabled) {
            return;
        }
        return queryClient.getQueryCache().subscribe((event) => {
            if (
                isShiftRecordedThisTickRef.current ||
                event.type !== "updated" ||
                event.action.type !== "success" ||
                !event.action.manual ||
                !loadedQueryHashesRef.current.has(event.query.queryHash)
            ) {
                return;
            }
            isShiftRecordedThisTickRef.current = true;
            queueMicrotask(() => {
                isShiftRecordedThisTickRef.current = false;
            });
            recordShift();
        });
    }, [queryClient, enabled, loadedQueryHashesRef, recordShift]);

    return {
        rows: merged?.rows,
        hasMore,
        isLoadingMore: slotResults.isAnyBatchPending,
        isLoadMoreError: slotResults.isAnyBatchError,
        isFirstPageFetching: firstPage.isFetching,
        loadNextPage,
        retry,
        loadedQueryKeys,
        resetKey,
    };
}
